import { Link } from 'react-router-dom';

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mt-12 mb-4 leading-snug">
      {children}
    </h2>
  );
}

function SubHeading({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="text-lg md:text-xl font-bold text-gray-800 mt-8 mb-3">
      {children}
    </h3>
  );
}

function Paragraph({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-gray-600 text-base leading-relaxed mb-4">
      {children}
    </p>
  );
}

function TipBox({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-teal-50 border-l-4 border-teal-400 rounded-r-xl px-6 py-5 my-6">
      <div className="flex items-start gap-3">
        <i className="ri-lightbulb-line text-teal-500 text-xl mt-0.5 shrink-0"></i>
        <p className="text-teal-800 text-sm leading-relaxed">{children}</p>
      </div>
    </div>
  );
}

interface UseCardProps {
  number: string;
  icon: string;
  title: string;
  description: string;
  imageUrl: string;
  imageAlt: string;
}

function UseCard({ number, icon, title, description, imageUrl, imageAlt }: UseCardProps) {
  return (
    <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden">
      <div className="w-full h-56 md:h-64 bg-gray-100 overflow-hidden">
        <img
          src={imageUrl}
          alt={imageAlt}
          className="w-full h-full object-cover object-top"
        />
      </div>
      <div className="p-6">
        <div className="flex items-start gap-4 mb-3">
          <div className="shrink-0 flex flex-col items-center gap-1">
            <div className="w-10 h-10 flex items-center justify-center bg-teal-50 rounded-xl">
              <i className={`${icon} text-teal-500 text-lg`}></i>
            </div>
            <span className="text-xs font-bold text-teal-400">{number}</span>
          </div>
          <div>
            <h4 className="font-bold text-gray-900 text-base leading-snug">{title}</h4>
          </div>
        </div>
        <p className="text-gray-500 text-sm leading-relaxed">{description}</p>
      </div>
    </div>
  );
}

const useCases: UseCardProps[] = [
  {
    number: '01',
    icon: 'ri-restaurant-line',
    title: 'Food & Beverage Areas',
    description: 'Stretch tents create inviting, shaded dining zones that keep guests comfortable while eating and drinking. Their open sides maintain airflow and visibility, allowing attendees to enjoy the festival ambiance while staying protected from the sun or light rain.',
    imageUrl: '/assets/img/f1227057f6e4.webp',
    imageAlt: 'Stretch tent over food and beverage area at festival',
  },
  {
    number: '02',
    icon: 'ri-music-line',
    title: 'Main Stage & Performance Areas',
    description: 'A large stretch tent over or beside a performance stage adds dramatic visual appeal while providing shade and rain cover for audiences. The unique organic shapes frame the stage beautifully and can be lit creatively for evening performances.',
    imageUrl: '/assets/img/bf8cb6185b03.webp',
    imageAlt: 'Stretch tent at festival performance stage area',
  },
  {
    number: '03',
    icon: 'ri-mental-health-line',
    title: 'Wellness & Yoga Zones',
    description: 'Dedicated wellness areas under stretch tents offer a tranquil escape from the festival energy. The natural colours and flowing shapes create an organic, calming atmosphere ideal for yoga sessions, meditation workshops, and holistic treatments.',
    imageUrl: '/assets/img/7eb1566f4d84.webp',
    imageAlt: 'Stretch tent wellness yoga zone at outdoor festival',
  },
  {
    number: '04',
    icon: 'ri-gallery-line',
    title: 'Art Installations & Galleries',
    description: 'Stretch tents make stunning art spaces. Their sweeping ceilings and flexible configurations allow for immersive gallery environments. Multiple joined tents can create flowing, interconnected exhibition spaces that surprise and delight visitors.',
    imageUrl: '/assets/img/7a6c28365956.webp',
    imageAlt: 'Stretch tent art gallery installation at festival',
  },
  {
    number: '05',
    icon: 'ri-gamepad-line',
    title: 'Kids & Family Zones',
    description: 'Create a safe, shaded, and easily supervised area for younger attendees. Stretch tents over kids\' areas provide essential sun and rain protection while the open design keeps sightlines clear for parents and staff monitoring the space.',
    imageUrl: '/assets/img/300de8060d05.webp',
    imageAlt: 'Stretch tent kids family zone at outdoor festival',
  },
  {
    number: '06',
    icon: 'ri-vip-crown-line',
    title: 'VIP & Glamping Experiences',
    description: 'Elevate premium areas with stretch tents that signal luxury and exclusivity. Used for VIP lounges, glamping bars, or backstage artist areas, stretch tents add an unmistakable sense of quality and sophistication that guests and performers appreciate.',
    imageUrl: '/assets/img/4100e37d0e4b.webp',
    imageAlt: 'Stretch tent VIP glamping area at festival',
  },
];

const benefitStats = [
  { value: '40%', label: 'Increase in dwell time under shaded areas' },
  { value: '3x', label: 'More social media sharing vs. standard marquees' },
  { value: '100%', label: 'Weather-flexible coverage for all event types' },
];

export default function ArticleBody() {
  return (
    <div className="max-w-4xl mx-auto px-6 py-16">

      {/* Hero image */}
      <div className="rounded-2xl overflow-hidden mb-10 w-full h-72 md:h-96 bg-gray-100">
        <img
          src="/assets/img/a1f9a721177f.webp"
          alt="Stretch tents enhancing festival appeal and guest experience"
          className="w-full h-full object-cover object-top"
        />
      </div>

      {/* Intro */}
      <Paragraph>
        Festivals are more than just music. They are immersive experiences where every detail contributes to how guests feel, move, and remember the event. One of the most powerful yet often overlooked tools for transforming a festival environment is the strategic placement of stretch tents. These versatile, visually stunning structures can completely change the character of a space, improving comfort, flow, and overall atmosphere.
      </Paragraph>
      <Paragraph>
        At Pop Pro Tents, we have seen first-hand how the right tent in the right location can elevate a good festival into an extraordinary one. In this article, we explore the most impactful ways to use stretch tents at your next festival to enhance your guests&apos; experience.
      </Paragraph>

      {/* Why stretch tents */}
      <SectionHeading>Why Stretch Tents Work So Well at Festivals</SectionHeading>
      <Paragraph>
        Unlike traditional square marquees or rigid frame tents, stretch tents are defined by their organic, free-flowing shapes. They can be configured in an almost unlimited variety of forms, from dramatic peaked canopies to low, intimate canopies hugging the ground. This flexibility makes them uniquely suited to festival environments where creativity and aesthetic impact matter as much as function.
      </Paragraph>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 my-8">
        {benefitStats.map((stat) => (
          <div key={stat.label} className="bg-gray-50 rounded-2xl p-6 text-center">
            <p className="text-3xl font-extrabold text-teal-500 mb-2">{stat.value}</p>
            <p className="text-gray-500 text-xs leading-snug">{stat.label}</p>
          </div>
        ))}
      </div>

      <TipBox>
        Stretch tents are inherently photogenic. Their bold silhouettes and rich colours consistently generate strong organic social media content, a significant bonus for festival marketing.
      </TipBox>

      {/* Key benefits */}
      <SectionHeading>Key Benefits of Using Stretch Tents at Festivals</SectionHeading>

      <SubHeading>Improved Crowd Flow &amp; Zone Definition</SubHeading>
      <Paragraph>
        One of the biggest challenges at large festivals is managing crowd movement and clearly defining different areas. Stretch tents excel at creating instantly recognisable zones. Their distinctive shapes act as visual landmarks that help guests navigate the site intuitively. Whether it&apos;s the yoga tent, the bar tent, or the kids&apos; zone, guests can spot their destination from a distance.
      </Paragraph>

      <SubHeading>Weather Protection Without Sacrificing Atmosphere</SubHeading>
      <Paragraph>
        Open-sided stretch tents offer shade and rain protection while maintaining the open-air festival feel that guests love. Unlike fully enclosed structures, stretch tents keep the energy flowing between different zones, allowing sound, light, and atmosphere to permeate the space naturally.
      </Paragraph>

      <SubHeading>Visual Impact &amp; Brand Expression</SubHeading>
      <Paragraph>
        Stretch tents are available in a wide range of colours and can be configured in unique shapes to reflect your festival&apos;s brand identity. Custom printed skins allow for logos, patterns, and artwork to be incorporated directly into the tent&apos;s fabric, turning functional structures into powerful brand expressions.
      </Paragraph>

      {/* Image break */}
      <div className="rounded-2xl overflow-hidden my-10 w-full h-64 md:h-80 bg-gray-100">
        <img
          src="/assets/img/b90e9d16f1e4.webp"
          alt="Festival guests enjoying shaded haven spa area under stretch tent"
          className="w-full h-full object-cover object-top"
        />
      </div>

      {/* Strategic uses */}
      <SectionHeading>6 Strategic Uses of Stretch Tents at Festivals</SectionHeading>
      <Paragraph>
        Based on our experience supplying stretch tents to major festivals across the country, including Lightning in a Bottle, Coachella-adjacent events, and large corporate festivals, here are the six most impactful placements:
      </Paragraph>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
        {useCases.map((uc) => (
          <UseCard key={uc.number} {...uc} />
        ))}
      </div>

      {/* Planning tips */}
      <SectionHeading>Planning Tips for Festival Tent Placement</SectionHeading>

      <SubHeading>Consider Sun Orientation</SubHeading>
      <Paragraph>
        Position tents so their open sides face away from the prevailing sun direction during peak hours. This maximises the shading benefit and keeps interior spaces comfortable throughout the day.
      </Paragraph>

      <SubHeading>Anchor Points &amp; Ground Conditions</SubHeading>
      <Paragraph>
        Stretch tents require solid anchor points, whether stakes in soft ground or ballast weights on hard surfaces. Always walk the site with your tent provider well in advance to identify the best anchor positions and ensure structural safety.
      </Paragraph>

      <SubHeading>Think About Traffic Flow</SubHeading>
      <Paragraph>
        Place high-traffic tents (bars, food, main stage) along natural pedestrian routes. Use lower-traffic tents (wellness, art, VIP) in slightly more secluded spots to create a sense of discovery and reward for guests who explore the full festival site.
      </Paragraph>

      <TipBox>
        Work with your stretch tent provider to map out the full festival site early. At Pop Pro Tents, we offer on-site consultations to help you optimise tent placement for maximum impact and guest flow.
      </TipBox>

      {/* Image break */}
      <div className="rounded-2xl overflow-hidden my-10 w-full h-64 md:h-80 bg-gray-100">
        <img
          src="/assets/img/d9f30ecc059a.webp"
          alt="Families enjoying festival experience under stretch tent structure"
          className="w-full h-full object-cover object-top"
        />
      </div>

      {/* Sustainability */}
      <SectionHeading>Sustainability &amp; Reusability</SectionHeading>
      <Paragraph>
        For festivals that prioritise environmental responsibility, stretch tents are an excellent choice. Our tents are built to last for hundreds of events, dramatically reducing the waste associated with single-use structures. Their lightweight design also means lower transport emissions compared to heavier traditional marquee systems.
      </Paragraph>
      <Paragraph>
        The fabric used in Pop Pro Tents stretch tents is UV-resistant, waterproof, and flame-retardant, meeting the safety and durability standards required for major public events while minimising environmental impact over the tent&apos;s lifespan.
      </Paragraph>

      {/* Conclusion */}
      <SectionHeading>Conclusion</SectionHeading>
      <Paragraph>
        Stretch tents are one of the most versatile and visually impactful tools available to festival organisers. When placed strategically, they improve crowd flow, provide essential weather protection, define distinct zones, and create the kind of memorable visual environments that guests talk about long after the event ends.
      </Paragraph>
      <Paragraph>
        Whether you are planning an intimate boutique festival or a large-scale multi-day event, Pop Pro Tents has the experience, the inventory, and the expertise to help you make the most of stretch tents across your entire festival site. Get in touch with our team today to start planning your event.
      </Paragraph>

      {/* Internal links */}
      <div className="flex flex-wrap gap-3 mt-6">
        <Link
          to="/products/standard-stretch-tents"
          className="inline-flex items-center gap-2 bg-teal-50 hover:bg-teal-100 text-teal-700 px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer"
        >
          <i className="ri-tent-line"></i>
          View Our Tents
        </Link>
        <Link
          to="/blog/stretch-tent-rentals-guide-tips-considerations"
          className="inline-flex items-center gap-2 bg-teal-50 hover:bg-teal-100 text-teal-700 px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer"
        >
          <i className="ri-book-open-line"></i>
          Rental Guide
        </Link>
        <Link
          to="/blog/lightning-in-a-bottle-festival"
          className="inline-flex items-center gap-2 bg-teal-50 hover:bg-teal-100 text-teal-700 px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer"
        >
          <i className="ri-article-line"></i>
          LIB Case Study
        </Link>
        <Link
          to="/contact"
          className="inline-flex items-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer"
        >
          <i className="ri-mail-line"></i>
          Inquire Now
        </Link>
      </div>

      {/* Tags */}
      <div className="flex flex-wrap gap-2 mt-10 pt-8 border-t border-gray-100">
        <span className="text-xs text-gray-400 mr-1 self-center">Tags:</span>
        {['Stretch Tents', 'Festivals', 'Event Planning', 'Guest Experience', 'Outdoor Events', 'Crowd Flow'].map((tag) => (
          <span
            key={tag}
            className="px-3 py-1.5 bg-gray-100 text-gray-600 text-xs rounded-full font-medium"
          >
            {tag}
          </span>
        ))}
      </div>
    </div>
  );
}
